import { FC, InputHTMLAttributes, ReactNode, useEffect, useState } from "react";
import classNames from "classnames";
import { useFormContext } from "react-hook-form";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { getValueByPath } from "utils";
import { FieldMessage } from "../fieldMessage/FieldMessage";

interface InputProps {
  disabled?: boolean;
  label: string;
  name: string;
  required?: boolean;
  type: string;
  placeholder?: string;
  size?: "sm" | "default";
  nullable?: boolean;
  inputSelector?: ReactNode;
  id?: string;
  onSelect?: () => void;
  compactErrorMessage?: boolean;
  opaqueOnHover?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly?: boolean;
  pattern?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  showError?: boolean;
}

export const Input: FC<InputProps> = ({
  disabled = false,
  label,
  name,
  required,
  type,
  placeholder,
  size = "default",
  nullable = false,
  inputSelector,
  id,
  onSelect,
  onChange,
  compactErrorMessage: _compactErrorMessage = false,
  readonly = false,
  pattern,
  onKeyDown,
  showError = true,
}) => {
  const [inputType, setInputType] = useState<InputHTMLAttributes<HTMLInputElement>["type"]>("text");
  const [isShowPassword, setIsShowPassword] = useState(false);

  const {
    register,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    setInputType(type);
  }, [type]);

  const number_type_canbe_empty = nullable && inputType === "number";
  const number_type_not_empty = !nullable && inputType === "number";

  const error = getValueByPath(errors, name);

  return (
    <div className={classNames("w-full flex flex-col", disabled && "cursor-not-allowed")}>
      <div className="relative">
        {label !== "" && (
          <div className="flex justify-between">
            <label
              htmlFor={name}
              className={classNames(
                "text-regular-2 text-muted-foreground",
                size === "sm" ? "pl-3" : "pl-4"
              )}
            >
              {label}:{required && <span className="text-primary"> *</span>}
            </label>
          </div>
        )}
        <div className="mt-2 relative flex flex-col gap-2">
          {inputType !== "textarea" ? (
            <>
              <input
                type={inputType}
                className={classNames(
                  "w-full text-foreground bg-muted rounded-xl",
                  "transition duration-200 ease-linear border border-input",
                  "appearance-none focus:outline-none focus:border-primary text-regular-2 text-left",
                  "disabled:cursor-not-allowed disabled:bg-muted disabled:text-foreground disabled:opacity-100",
                  "read-only:cursor-default",
                  "placeholder:text-muted-foreground/70",
                  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                  size === "sm" ? "px-4 py-2.5 min-h-[42px]" : "px-5 py-3 min-h-[48px]",
                  type === "password" && "pr-16"
                )}
                disabled={disabled}
                readOnly={readonly}
                autoComplete="on"
                onKeyDown={onKeyDown}
                {...register(name, {
                  setValueAs: (v) => {
                    return (v === null || v === "") && nullable
                      ? null
                      : number_type_canbe_empty
                        ? Number(v)
                        : v;
                  },
                  valueAsNumber: number_type_not_empty,
                  onChange,
                })}
                placeholder={placeholder}
                pattern={pattern}
              />
              {inputSelector && inputSelector}
            </>
          ) : (
            <textarea
              id={id}
              rows={size === "sm" ? 3 : 5}
              className={classNames(
                "hide-scrollbar",
                "w-full text-foreground h-auto bg-muted rounded-xl",
                "transition duration-200 ease-linear border border-input",
                "appearance-none focus:outline-none focus:border-primary text-regular-2 p-8 py-5 text-left",
                "disabled:cursor-not-allowed disabled:bg-muted disabled:text-foreground disabled:opacity-100",
                "placeholder:text-muted-foreground/70",
                type === "password" && "pr-16"
              )}
              disabled={disabled}
              autoComplete="on"
              {...register(name)}
              placeholder={placeholder}
              onSelect={onSelect}
            />
          )}

          {type === "password" && (
            <div className="absolute h-full right-2 bg-none px-4 top-0 flex items-center text-xl cursor-pointer text-muted-foreground hover:text-foreground">
              {!isShowPassword ? (
                <div
                  onClick={() => {
                    setIsShowPassword(!isShowPassword);
                    setInputType("text");
                  }}
                >
                  <AiOutlineEye />
                </div>
              ) : (
                <div
                  onClick={() => {
                    setIsShowPassword(!isShowPassword);
                    setInputType("password");
                  }}
                >
                  <AiOutlineEyeInvisible />
                </div>
              )}
            </div>
          )}
        </div>
        {showError && error && (
          <FieldMessage message={error.message?.toString()} type="warning" align="left" />
        )}
      </div>
    </div>
  );
};
